import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ExamScoresModal from '../../Components/ExamScoresModal';
import InviteCandidatesModal from '../../Components/InviteCandidatesModal';
import ExamInvitationsModal from '../../Components/ExamInvitationsModal';
import './ExamAdmin.css';

const EXAM_API_URL = 'https://api.avtechbmt.cloud/api/exams';

/* ------------------------------------------------------------------
   Helper — describe the schedule state of an exam.
   ------------------------------------------------------------------ */
function getScheduleState(exam) {
  const now = Date.now();
  const start = exam.scheduledStartTime
    ? new Date(exam.scheduledStartTime).getTime()
    : null;
  const end = exam.scheduledEndTime
    ? new Date(exam.scheduledEndTime).getTime()
    : null;

  if (!start && !end) return { label: 'Always available', tone: 'none' };

  if (start && now < start) {
    return {
      label: `Opens ${new Date(start).toLocaleString()}`,
      tone: 'upcoming',
    };
  }
  if (end && now >= end) {
    return {
      label: `Closed ${new Date(end).toLocaleString()}`,
      tone: 'closed',
    };
  }
  if (start && end) {
    return {
      label: `Open until ${new Date(end).toLocaleString()}`,
      tone: 'open',
    };
  }
  return { label: 'Scheduled', tone: 'upcoming' };
}

/* Count snippet MCQs (questions that have a codeSnippet) */
function countSnippetMcqs(exam) {
  if (!exam?.questions?.length) return 0;
  return exam.questions.filter(
    (q) => q.type === 'mcq' && q.codeSnippet && q.codeSnippet.trim()
  ).length;
}

export default function ExamAdmin({ onEditExam, onCreateExam }) {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Toolbar
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [scheduleFilter, setScheduleFilter] = useState('all');

  // Time adjust inline
  const [adjustingExamId, setAdjustingExamId] = useState(null);
  const [extraMinsInput, setExtraMinsInput] = useState(5);

  // Results modal
  const [selectedExamResults, setSelectedExamResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  // Invite & Invitations modals
  const [invitingExam, setInvitingExam] = useState(null);
  const [viewingInvitations, setViewingInvitations] = useState(null);

  // Per-exam loading state when auto-activating
  const [activatingExamId, setActivatingExamId] = useState(null);

  // Success popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  /* ============================================================
     HELPERS
     ============================================================ */

  const getAuthHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token')}`,
    }),
    [token]
  );

  /* ============================================================
     FETCH
     ============================================================ */

  const fetchExams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${EXAM_API_URL}/admin/all`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setExams(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch exams.');
      }
    } catch {
      setError('Error connecting to backend server.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  /* Auto-dismiss success */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  /* Body scroll lock for popups/modals */
  useEffect(() => {
    if (
      showSuccessPopup ||
      selectedExamResults ||
      invitingExam ||
      viewingInvitations
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSuccessPopup, selectedExamResults, invitingExam, viewingInvitations]);

  /* ============================================================
     DERIVED
     ============================================================ */

  const visibleExams = useMemo(() => {
    let list = [...exams];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.title?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'active') list = list.filter((e) => e.isActive);
    if (statusFilter === 'inactive') list = list.filter((e) => !e.isActive);

    if (scheduleFilter !== 'all') {
      const now = Date.now();
      list = list.filter((e) => {
        const start = e.scheduledStartTime
          ? new Date(e.scheduledStartTime).getTime()
          : null;
        const end = e.scheduledEndTime
          ? new Date(e.scheduledEndTime).getTime()
          : null;
        if (scheduleFilter === 'none') return !start && !end;
        if (scheduleFilter === 'upcoming') return start && now < start;
        if (scheduleFilter === 'open')
          return (!start || now >= start) && end && now < end;
        if (scheduleFilter === 'closed') return end && now >= end;
        return true;
      });
    }

    return list;
  }, [exams, search, statusFilter, scheduleFilter]);

  const stats = useMemo(() => {
    const total = exams.length;
    const active = exams.filter((e) => e.isActive).length;
    const totalQuestions = exams.reduce(
      (sum, e) => sum + (e.questions?.length || 0),
      0
    );
    const totalMinutes = exams.reduce(
      (sum, e) => sum + (Number(e.durationInMinutes) || 0),
      0
    );
    const scheduled = exams.filter(
      (e) => e.scheduledStartTime || e.scheduledEndTime
    ).length;
    const snippetMcqs = exams.reduce(
      (sum, e) => sum + countSnippetMcqs(e),
      0
    );
    return {
      total,
      active,
      totalQuestions,
      totalMinutes,
      scheduled,
      snippetMcqs,
    };
  }, [exams]);

  /* ============================================================
     NAVIGATION
     ============================================================ */

  const handleEditClick = (exam) => {
    if (onEditExam) {
      onEditExam(exam._id);
    } else {
      navigate(`/admin/exams/edit/${exam._id}`);
    }
  };

  const handleCreateClick = () => {
    if (onCreateExam) {
      onCreateExam();
    } else {
      navigate('/admin/exams/create');
    }
  };

  /* ============================================================
     CARD ACTIONS
     ============================================================ */

  const handleDeleteExam = async (examId, examTitle) => {
    if (
      !window.confirm(
        `Delete "${examTitle}"?\n\nThis will also remove all student submissions and invitations. This cannot be undone.`
      )
    )
      return;

    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${EXAM_API_URL}/${examId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess('Exam deleted successfully.');
        setExams((prev) => prev.filter((e) => e._id !== examId));
      } else {
        setError(data.message || 'Failed to delete exam.');
      }
    } catch {
      setError('Network error trying to delete exam.');
    }
  };

  const toggleExamStatus = async (exam) => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${EXAM_API_URL}/${exam._id}/toggle-status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !exam.isActive }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(`Exam is now ${!exam.isActive ? 'Active' : 'Inactive'}.`);
        fetchExams();
      } else {
        setError(data.message || 'Failed to toggle status.');
      }
    } catch {
      setError('Failed to update status.');
    }
  };

  const handleTimeAdjustment = async (examId) => {
    try {
      const res = await fetch(`${EXAM_API_URL}/${examId}/adjust-time`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ extraMinutes: Number(extraMinsInput) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(data.message || 'Time adjusted successfully.');
        setAdjustingExamId(null);
        fetchExams();
      } else {
        setError(data.message || 'Failed to adjust time.');
      }
    } catch {
      setError('Network error adjusting time.');
    }
  };

  const handleViewResults = async (examId) => {
    setResultsLoading(true);
    setError('');
    try {
      const res = await fetch(`${EXAM_API_URL}/${examId}/results`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSelectedExamResults(data);
      } else {
        setError(data.message || 'Failed to fetch exam results.');
      }
    } catch {
      setError('Error connecting to backend server.');
    } finally {
      setResultsLoading(false);
    }
  };

  /* ============================================================
     INVITE — AUTO-ACTIVATE IF NEEDED
     ============================================================ */

  const handleInviteClick = async (exam) => {
    setError('');
    setSuccess('');

    if (exam.isActive) {
      setInvitingExam(exam);
      return;
    }

    setActivatingExamId(exam._id);
    try {
      const res = await fetch(`${EXAM_API_URL}/${exam._id}/toggle-status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: true }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.message || 'Failed to activate exam before inviting.');
        return;
      }

      setExams((prev) =>
        prev.map((e) => (e._id === exam._id ? { ...e, isActive: true } : e))
      );

      setSuccess(`"${exam.title}" activated. You can now send invitations.`);
      setInvitingExam({ ...exam, isActive: true });
    } catch {
      setError('Network error while activating exam.');
    } finally {
      setActivatingExamId(null);
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <div className="ExamAdmin-container">
      {/* HEADER */}
      <div className="ExamAdmin-header">
        <div>
          <span className="ExamAdmin-field-label">
            05 / Assessment Center
          </span>
          <h2>Exam Management</h2>
          <p className="ExamAdmin-header-sub">
            Create, edit, and monitor all assessments. Click any exam to edit
            it on a dedicated studio page.
          </p>
        </div>
        <button
          className="ExamAdmin-cta-primary"
          onClick={handleCreateClick}
        >
          ＋ Create New Exam
        </button>
      </div>

      {/* ALERTS */}
      {error && (
        <div className="ExamAdmin-alert-box ExamAdmin-alert-error">
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div className="ExamAdmin-alert-box ExamAdmin-alert-success">
          ✅ {success}
        </div>
      )}

      {/* KPI STRIP */}
      <div className="ExamAdmin-kpi-strip">
        <div className="ExamAdmin-kpi">
          <span>Total Exams</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="ExamAdmin-kpi">
          <span>Active</span>
          <strong className="kpi-green">{stats.active}</strong>
        </div>
        <div className="ExamAdmin-kpi">
          <span>Total Questions</span>
          <strong className="kpi-blue">{stats.totalQuestions}</strong>
        </div>
        <div className="ExamAdmin-kpi">
          <span>Total Duration</span>
          <strong className="kpi-amber">{stats.totalMinutes} min</strong>
        </div>
        <div className="ExamAdmin-kpi">
          <span>Scheduled</span>
          <strong className="kpi-purple">{stats.scheduled}</strong>
        </div>
        <div className="ExamAdmin-kpi">
          <span>Snippet MCQs</span>
          <strong className="kpi-blue">{stats.snippetMcqs}</strong>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="ExamAdmin-toolbar">
        <input
          className="ExamAdmin-form-input ExamAdmin-toolbar-search"
          placeholder="🔍 Search exams by title or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="ExamAdmin-form-input"
          style={{ maxWidth: 180 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <select
          className="ExamAdmin-form-input"
          style={{ maxWidth: 200 }}
          value={scheduleFilter}
          onChange={(e) => setScheduleFilter(e.target.value)}
        >
          <option value="all">All Schedules</option>
          <option value="none">No Schedule</option>
          <option value="upcoming">Upcoming</option>
          <option value="open">Currently Open</option>
          <option value="closed">Closed</option>
        </select>

        <button
          className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
          onClick={fetchExams}
          disabled={loading}
        >
          ↻ Refresh
        </button>
      </div>

      {/* EXAMS LIST */}
      {loading ? (
        <div className="ExamAdmin-state-text">
          <span className="ExamAdmin-loading-spinner" />
          <span>Loading assessments…</span>
        </div>
      ) : visibleExams.length === 0 ? (
        <div className="ExamAdmin-state-text">
          <span className="icon">📝</span>
          <span>
            {exams.length === 0
              ? 'No exams created yet. Click "Create New Exam" to get started.'
              : 'No exams match your filters.'}
          </span>
        </div>
      ) : (
        <div className="ExamAdmin-exams-list">
          {visibleExams.map((exam) => {
            const isActivating = activatingExamId === exam._id;
            const schedule = getScheduleState(exam);
            const snippetCount = countSnippetMcqs(exam);
            return (
              <div
                key={exam._id}
                className={`ExamAdmin-exam-item-card ${
                  !exam.isActive ? 'is-inactive' : ''
                }`}
              >
                <div className="ExamAdmin-exam-info">
                  <h3
                    className="ExamAdmin-exam-title ExamAdmin-clickable"
                    onClick={() => handleEditClick(exam)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleEditClick(exam);
                      }
                    }}
                  >
                    {exam.title}
                  </h3>

                  <p className="ExamAdmin-exam-description">
                    {exam.description || 'No description provided.'}
                  </p>

                  <div className="ExamAdmin-exam-metadata">
                    <span className="ExamAdmin-meta-pill">
                      ⏱️ {exam.durationInMinutes} min
                      {exam.extraMinutesAdded
                        ? ` (+${exam.extraMinutesAdded})`
                        : ''}
                    </span>
                    <span className="ExamAdmin-meta-pill">
                      🎯 Pass: {exam.passingMarks || 0}
                    </span>
                    <span className="ExamAdmin-meta-pill">
                      ❓ {exam.questions?.length || 0} question
                      {exam.questions?.length === 1 ? '' : 's'}
                    </span>

                    {snippetCount > 0 && (
                      <span className="ExamAdmin-meta-pill is-snippet">
                        💻 {snippetCount} snippet MCQ
                        {snippetCount === 1 ? '' : 's'}
                      </span>
                    )}

                    <span
                      className={`ExamAdmin-meta-pill ${
                        exam.accessType === 'public'
                          ? 'is-public'
                          : 'is-private'
                      }`}
                    >
                      {exam.accessType === 'public'
                        ? '🌐 Public'
                        : '🔒 Private'}
                    </span>
                    <span
                      className={`ExamAdmin-meta-pill ${
                        exam.isActive ? 'is-active' : 'is-inactive'
                      }`}
                    >
                      ● {exam.isActive ? 'Active' : 'Inactive'}
                    </span>

                    {schedule.tone !== 'none' && (
                      <span
                        className={`ExamAdmin-meta-pill schedule-pill schedule-${schedule.tone}`}
                        title={schedule.label}
                      >
                        🗓️ {schedule.label}
                      </span>
                    )}
                  </div>
                </div>

                <div className="ExamAdmin-exam-actions">
                  <button
                    className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
                    onClick={() => handleEditClick(exam)}
                  >
                    ✏️ Edit
                  </button>

                  <button
                    className="ExamAdmin-cta-primary ExamAdmin-btn-compact"
                    onClick={() => handleInviteClick(exam)}
                    disabled={isActivating}
                    title={
                      exam.isActive
                        ? 'Send email invitations to candidates'
                        : 'Will activate the exam and open the invite dialog'
                    }
                  >
                    {isActivating ? '⏳ Activating…' : '✉️ Invite'}
                  </button>

                  <button
                    className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
                    onClick={() => setViewingInvitations(exam)}
                  >
                    👥 Invitations
                  </button>

                  <button
                    className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
                    onClick={() => handleViewResults(exam._id)}
                    disabled={resultsLoading}
                  >
                    📊 Scores
                  </button>

                  <button
                    className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
                    onClick={() => toggleExamStatus(exam)}
                  >
                    {exam.isActive ? '🚫 Deactivate' : '✅ Activate'}
                  </button>

                  {adjustingExamId === exam._id ? (
                    <div className="ExamAdmin-time-adjust-controls">
                      <input
                        type="number"
                        value={extraMinsInput}
                        onChange={(e) => setExtraMinsInput(e.target.value)}
                        className="ExamAdmin-form-input ExamAdmin-input-mini"
                        min="1"
                      />
                      <button
                        className="ExamAdmin-cta-primary ExamAdmin-btn-mini"
                        onClick={() => handleTimeAdjustment(exam._id)}
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => setAdjustingExamId(null)}
                        className="ExamAdmin-btn-close-mini"
                        aria-label="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      className="ExamAdmin-cta-secondary ExamAdmin-btn-compact"
                      onClick={() => setAdjustingExamId(exam._id)}
                    >
                      ⏱ Adjust Time
                    </button>
                  )}

                  <button
                    className="ExamAdmin-btn-delete ExamAdmin-btn-compact"
                    onClick={() => handleDeleteExam(exam._id, exam.title)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SUCCESS POPUP */}
      {showSuccessPopup && (
        <div
          className="ExamAdmin-success-popup-overlay"
          onClick={() => setShowSuccessPopup(false)}
        >
          <div
            className="ExamAdmin-success-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ExamAdmin-success-icon">✓</div>
            <h3>Success!</h3>
            <p>{successMessage}</p>
            <button
              className="ExamAdmin-cta-primary"
              onClick={() => setShowSuccessPopup(false)}
            >
              Great, thanks!
            </button>
          </div>
        </div>
      )}

      {/* RESULTS MODAL */}
      {selectedExamResults && (
        <ExamScoresModal
          results={selectedExamResults}
          onClose={() => setSelectedExamResults(null)}
        />
      )}

      {/* INVITE MODAL */}
      {invitingExam && (
        <InviteCandidatesModal
          exam={invitingExam}
          onClose={() => setInvitingExam(null)}
          onSuccess={(msg) => setSuccess(msg)}
        />
      )}

      {/* VIEW INVITATIONS MODAL */}
      {viewingInvitations && (
        <ExamInvitationsModal
          exam={viewingInvitations}
          onClose={() => setViewingInvitations(null)}
        />
      )}
    </div>
  );
}